import {
  FormControl,
  FormHelperText,
  FormLabel,
  styled,
  ToggleButton,
  ToggleButtonGroup,
  ToggleButtonGroupProps,
  useThemeProps,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import {
  FORM_FIELD_THEME_NAME,
  FORM_TOGGLE_GROUP_THEME_NAME,
} from '@onevision-resources/design-theme';
import { useId } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { DynamicIcon, type IconLibrary } from 'src/components/DynamicIcon/dynamic-icon';
import type { CommonFormFieldProps } from 'src/components/Forms/DynamicForm/dynamic-form';
import { FormFieldLabel } from 'src/components/Forms/FormFieldLabel/form-field-label';
import { useWatchConditions } from 'src/components/Forms/use-validations';

export interface FormToggleGroupOption {
  id: string;
  value: string;
  label: string;
  icon?: string;
  iconLibrary?: IconLibrary;
  testId?: string;
}

/**
 * Deliberately narrow rather than an `Omit<ToggleButtonGroupProps, …>`: this type
 * joins the `FormField` union, and `get-form-field` destructures shared props off
 * that union — every extra MUI prop pulled in here collides with another member's
 * (`size` alone: MUI's toggle allows `large`, the form control does not).
 * Group-level props go through `toggleGroupProps`.
 */
export type FormToggleGroupProps = {
  label?: string;
  options: FormToggleGroupOption[];
  value?: string;
  /** Lets any number of options be on at once; the field value is then the array of their values. */
  multiple?: boolean;
  disabled?: boolean;
  onChange?: ToggleButtonGroupProps['onChange'];
  sx?: SxProps<Theme>;
  toggleGroupProps?: Partial<ToggleButtonGroupProps>;
} & CommonFormFieldProps<'toggle-group', string | string[]>;

const ICON_CLASS_NAME = 'form-toggle-group-icon';

const FieldToggleGroup = styled(ToggleButtonGroup, {
  name: FORM_TOGGLE_GROUP_THEME_NAME,
  slot: 'Root',
})({});

/**
 * Selected toggles fill with the brand teal so on and off read apart at a glance —
 * a neutral grey fill left people clicking each day to learn which state was which
 * (1V-6121). The theme paints selected toggles with the secondary purple through a
 * `.MuiToggleButtonGroup-root .Mui-selected` rule, which outranks an `sx` on the
 * button itself — so the override has to be set here, at the group level.
 */
const groupSx: SxProps<Theme> = {
  alignSelf: 'flex-start',
  '& .MuiToggleButton-root': {
    borderColor: (theme) => theme.palette.custom.OV.sys.outlineVariant,
  },
  '& .MuiButtonBase-root:hover': {
    backgroundColor: (theme) => theme.palette.custom.OV.sys.surfaceContainerHigh,
  },
  '& .MuiButtonBase-root.Mui-selected': {
    backgroundColor: (theme) => theme.palette.custom.OV.sys.primary,
    borderColor: (theme) => theme.palette.custom.OV.sys.primary,
    color: (theme) => theme.palette.custom.OV.sys.onPrimary,
    [`& .${ICON_CLASS_NAME}`]: {
      color: 'inherit',
    },
    '&:hover': {
      backgroundColor: (theme) => theme.palette.custom.OV.sys.primary,
    },
    '&.Mui-disabled': {
      opacity: 0.38,
    },
  },
};

function getGroupValue(value: unknown, multiple: boolean): string | string[] {
  if (multiple) {
    return Array.isArray(value)
      ? value.filter((item): item is string => typeof item === 'string')
      : [];
  }
  return typeof value === 'string' ? value : '';
}

export const FormToggleGroup = (inProps: FormToggleGroupProps) => {
  const props = useThemeProps({ props: inProps, name: FORM_FIELD_THEME_NAME });
  const {
    name,
    label,
    options,
    required,
    defaultValue,
    disabled,
    showsIf = [],
    onChange,
    sx,
    toggleGroupProps,
    multiple = false,
    labelAbove = false,
    'data-testid': testId,
  } = props;
  const labelId = useId();

  const { control, formState } = useFormContext();
  const errorMsg = formState.errors[name]?.message;
  const hasErrorMsg = typeof errorMsg === 'string';

  const shouldShow = useWatchConditions({ control, showsIf });

  if (!shouldShow) {
    return null;
  }

  return (
    <FormControl error={hasErrorMsg} sx={sx} data-testid={testId}>
      {!!label &&
        (labelAbove ? (
          <FormFieldLabel id={labelId} disabled={disabled}>
            {label}
          </FormFieldLabel>
        ) : (
          <FormLabel sx={{ fontSize: '0.75rem', marginBottom: 0.5 }}>{label}</FormLabel>
        ))}
      <Controller
        name={name}
        control={control}
        rules={{ required }}
        defaultValue={defaultValue ?? (multiple ? [] : (options[0]?.value ?? ''))}
        render={({ field }) => (
          <FieldToggleGroup
            exclusive={!multiple}
            size="small"
            disabled={disabled}
            aria-labelledby={labelAbove && !!label ? labelId : undefined}
            {...toggleGroupProps}
            value={getGroupValue(field.value, multiple)}
            onChange={(event, nextValue) => {
              // An exclusive choice has no empty state: deselecting the active button
              // yields null, and keeping the current value is the only sane answer.
              if (!multiple && typeof nextValue !== 'string') {
                return;
              }

              field.onChange(nextValue);
              onChange?.(event, nextValue);
            }}
            sx={groupSx}
          >
            {options.map((option) => (
              <ToggleButton
                key={option.id}
                value={option.value}
                data-testid={option.testId ?? `${name}-${option.value}`}
                sx={{
                  gap: 0.75,
                  textTransform: 'none',
                  paddingX: 2,
                  color: (theme) => theme.palette.custom.OV.sys.onSurfaceVariant,
                }}
              >
                {!!option.icon && (
                  <DynamicIcon
                    className={ICON_CLASS_NAME}
                    name={option.icon}
                    library={option.iconLibrary}
                  />
                )}
                {option.label}
              </ToggleButton>
            ))}
          </FieldToggleGroup>
        )}
      />
      {hasErrorMsg ? <FormHelperText>{errorMsg}</FormHelperText> : null}
    </FormControl>
  );
};
